<template>
    <DModal
        :show="showModal"
        :is-awaiting-validation="true"
        :title="__('tools.testing.snapshots.create.modal.title')"
        :message="__('tools.testing.snapshots.create.modal.message')"
        is-confirm
        is-medium
        :disabled-action="submitBlocked"
        @close="close"
        @confirm="confirm"
    >
        <div class="modal-content px-4 mb-4">
            <DAlert
                v-if="errors && errors.general_error"
                color="danger"
                class="mt-2"
            >
                {{ errors.general_error }}
            </DAlert>
            <DFormRow
                :label="__('tools.testing.snapshots.create.modal.form.settings')"
                is-required
                :error="errors?.settings"
                :hint="__('tools.testing.snapshots.create.modal.form.settings_hint')"
            >
                <div class="d-flex align-items-center gap-2 flex-wrap">
                    <DSwitch
                        id="settings"
                        v-model="form.settings"
                        color="success"
                    />
                    <DButton
                        variant="ghost"
                        :class="{ invisible: !form.settings }"
                        mini
                        color="primary"
                        :label="configureButtonLabel('settings')"
                        @click="openConfigModal('settings')"
                    />
                    <small
                        v-if="form.settings"
                        class="text-muted"
                    >{{ sectionSummary('settings') }}</small>
                </div>
            </DFormRow>
            <DFormRow
                :label="__('tools.testing.snapshots.create.modal.form.products')"
                is-required
                :error="errors?.products ?? errors?.retirement_status"
                :hint="__('tools.testing.snapshots.create.modal.form.products_hint')"
            >
                <div class="d-flex align-items-center gap-2 flex-wrap">
                    <DSwitch
                        id="products"
                        v-model="form.products"
                        color="success"
                    />
                    <DButton
                        variant="ghost"
                        :class="{ invisible: !form.products }"
                        mini
                        color="primary"
                        :label="configureButtonLabel('products')"
                        @click="openConfigModal('products')"
                    />
                    <small
                        v-if="form.products"
                        class="text-muted"
                    >{{ sectionSummary('products') }}</small>
                </div>
            </DFormRow>
            <DFormRow
                :label="__('tools.testing.snapshots.create.modal.form.email_templates')"
                is-required
                :error="errors?.email_templates"
                :hint="__('tools.testing.snapshots.create.modal.form.email_templates_hint')"
            >
                <div class="d-flex align-items-center gap-2 flex-wrap">
                    <DSwitch
                        id="email_templates"
                        v-model="form.emailTemplates"
                        color="success"
                    />
                    <DButton
                        variant="ghost"
                        :class="{ invisible: !form.emailTemplates }"
                        mini
                        color="primary"
                        :label="configureButtonLabel('email_templates')"
                        @click="openConfigModal('email_templates')"
                    />
                    <small
                        v-if="form.emailTemplates"
                        class="text-muted"
                    >{{ sectionSummary('email_templates') }}</small>
                </div>
            </DFormRow>
            <DFormRow
                :label="__('tools.testing.snapshots.create.modal.form.sms_templates')"
                is-required
                :error="errors?.sms_templates"
                :hint="__('tools.testing.snapshots.create.modal.form.sms_templates_hint')"
            >
                <div class="d-flex align-items-center gap-2 flex-wrap">
                    <DSwitch
                        id="sms_templates"
                        v-model="form.smsTemplates"
                        color="success"
                    />
                    <DButton
                        variant="ghost"
                        :class="{ invisible: !form.smsTemplates }"
                        mini
                        color="primary"
                        :label="configureButtonLabel('sms_templates')"
                        @click="openConfigModal('sms_templates')"
                    />
                    <small
                        v-if="form.smsTemplates"
                        class="text-muted"
                    >{{ sectionSummary('sms_templates') }}</small>
                </div>
            </DFormRow>
            <DFormRow
                :label="__('tools.testing.snapshots.create.modal.form.customers')"
                is-required
                :error="errors?.customers"
                :hint="__('tools.testing.snapshots.create.modal.form.customers_hint')"
            >
                <div class="d-flex align-items-center gap-2 flex-wrap">
                    <DSwitch
                        id="customers"
                        v-model="form.customers"
                        color="success"
                    />
                    <DButton
                        variant="ghost"
                        :class="{ invisible: !form.customers }"
                        mini
                        color="primary"
                        :label="configureButtonLabel('customers')"
                        @click="openConfigModal('customers')"
                    />
                    <small
                        v-if="form.customers"
                        class="text-muted"
                    >{{ sectionSummary('customers') }}</small>
                </div>
                <CustomerSectionCounts
                    v-if="form.customers"
                    class="mt-2"
                    :status="status"
                    :counts="counts"
                    :limits="limits"
                />
            </DFormRow>
            <DFormRow
                :label="__('tools.testing.snapshots.create.modal.form.include_secrets')"
                is-required
                :error="errors?.includeSecrets"
                :hint="__('tools.testing.snapshots.create.modal.form.include_secrets_hint')"
            >
                <DSwitch
                    id="products"
                    v-model="form.includeSecrets"
                    color="success"
                    :disabled="excludeSecrets"
                />
            </DFormRow>
            <DFormRow
                :label="__('tools.testing.snapshots.create.modal.form.name')"
                :error="errors?.displayName"
                :hint="__('tools.testing.snapshots.create.modal.form.name_hint')"
            >
                <DInput
                    id="display_name"
                    v-model="form.displayName"
                />
            </DFormRow>
        </div>
    </DModal>

    <SectionConfigModal
        v-if="configModalSection !== null"
        :show-modal="configModalSection !== null"
        :section="configModalSection"
        :current-config="sectionConfig"
        :product-groups="productGroups"
        :fulfillment-account-groups="fulfillmentAccountGroups"
        :email-languages="emailLanguages"
        :sms-languages="smsLanguages"
        :settings-models="settingsModels"
        :product-models="productModels"
        :limits="limits"
        @close="closeConfigModal"
        @apply="applyConfigModal"
    />
</template>

<script setup>

import { computed, reactive, ref } from 'vue'
import { __ } from '~@/Composables/translation'
import { useForm } from '@inertiajs/vue3'
import SectionConfigModal from '~@/Components/Tools/Testing/Snapshots/SectionConfigModal'
import CustomerSectionCounts from '~@/Components/Tools/Testing/Snapshots/CustomerSectionCounts'
import { getSnapshotSectionSummary, hasSnapshotSectionFilters } from '~@/Composables/snapshotSectionSummary'
import { useSnapshotSectionCounts } from '~@/Composables/snapshotSectionCounts'

defineProps({
    showModal: {
        type: Boolean,
        default: false
    },
    excludeSecrets: {
        type: Boolean,
        default: false
    },
    errors: {
        type: Object,
        default: () => { return {} }
    },
    productGroups: {
        type: Array,
        default: () => { return [] }
    },
    fulfillmentAccountGroups: {
        type: Array,
        default: () => { return [] }
    },
    emailLanguages: {
        type: Array,
        default: () => { return [] }
    },
    smsLanguages: {
        type: Array,
        default: () => { return [] }
    },
    settingsModels: {
        type: Array,
        default: () => { return [] }
    },
    productModels: {
        type: Array,
        default: () => { return [] }
    },
    limits: {
        type: Object,
        default: () => { return {} }
    },
})

const emit = defineEmits(['close', 'confirm'])

const form = useForm({
    settings: true,
    products: true,
    emailTemplates: true,
    smsTemplates: true,
    // Off by default: the section is meant for a handful of named test customers, and on any real tenant the whole of
    // it is over the limits, so a default of on opened the modal with Confirm disabled.
    customers: false,
    includeSecrets: false,
    displayName: ''
})

const sectionConfig = reactive({
    productGroupIds: null,
    retirementStatus: null,
    fulfillmentAccountGroupIds: null,
    emailLanguages: null,
    smsLanguages: null,
    customerIds: null,
    settingsModels: null,
    productModels: null,
})

const configModalSection = ref(null)

// Counted for the IDs that have actually been applied, which is what a create would be measured on -- the config modal
// counts its own in-progress input separately. Not debounced: neither trigger is typing.
const { status, counts, isRefused } = useSnapshotSectionCounts(
    () => sectionConfig.customerIds,
    { enabled: () => form.customers }
)

// Only a verdict the server actually returned blocks the button; a count that has not run, is running or failed
// leaves it alive (see GetSnapshotSectionCountsController for why).
const submitBlocked = computed(() => form.customers && isRefused.value)

function openConfigModal (section) {
    configModalSection.value = section
}

function closeConfigModal () {
    configModalSection.value = null
}

function applyConfigModal (config) {
    Object.assign(sectionConfig, config)
    configModalSection.value = null
}

function hasActiveFilters (section) {
    return hasSnapshotSectionFilters(section, sectionConfig)
}

function configureButtonLabel (section) {
    return hasActiveFilters(section)
        ? __('tools.testing.snapshots.create.modal.form.configure_active')
        : __('tools.testing.snapshots.create.modal.form.configure')
}

function sectionSummary (section) {
    return getSnapshotSectionSummary(section, sectionConfig)
}

function confirm () {
    emit(
        'confirm',
        {
            settings: form.settings,
            products: form.products,
            emailTemplates: form.emailTemplates,
            smsTemplates: form.smsTemplates,
            customers: form.customers,
            includeSecrets: form.includeSecrets,
            displayName: form.displayName,
            productGroupIds: sectionConfig.productGroupIds,
            retirementStatus: sectionConfig.retirementStatus,
            fulfillmentAccountGroupIds: sectionConfig.fulfillmentAccountGroupIds,
            emailLanguages: sectionConfig.emailLanguages,
            smsLanguages: sectionConfig.smsLanguages,
            customerIds: sectionConfig.customerIds,
            settingsModels: sectionConfig.settingsModels,
            productModels: sectionConfig.productModels,
        }
    )
}

function close () {
    emit('close')
    form.reset()
    Object.assign(sectionConfig, {
        productGroupIds: null,
        retirementStatus: null,
        fulfillmentAccountGroupIds: null,
        emailLanguages: null,
        smsLanguages: null,
        customerIds: null,
        settingsModels: null,
        productModels: null,
    })
}

</script>

<style lang="scss" scoped>
.modal-content {
    box-shadow: none;
}

.modal-content h2, .modal-content p {
    color: var(--cui-body-color);
    opacity: 0.6;
}
</style>
